import { useMemo, useState } from 'react';
import { VegaButton, VegaFlex, VegaPageNotification } from '@globalpayments/vega-react';

import PageSkeleton from '../../components/pageSkeleton';
import { useHorizonOptOutTracker } from '../../hooks/useHorizonOptOutTracker';
import { ALL_HORIZON_REGIONS, ALL_TRIGGERED_BY } from '../../types/horizonOptOutTracker';
import type {
  HorizonOptOutDateRange,
  HorizonOptOutDisplayRow,
  HorizonRegionFilter,
  HorizonTriggeredByFilter,
} from '../../types/horizonOptOutTracker';
import { describeHorizonOptOutFilters, getDefaultHorizonOptOutDateRange } from '../../utility/horizonOptOutTracker';
import { HorizonOptOutFilters } from './components/HorizonOptOutFilters';
import { HorizonOptOutSummary } from './components/HorizonOptOutSummary';
import { HorizonOptOutTable } from './components/HorizonOptOutTable';

const sortByOptOutTimeDescending = (rows: HorizonOptOutDisplayRow[]) =>
  [...rows].sort((left, right) => {
    const leftTime = left.detailStatus === 'loaded' && left.optOutTimeUTC ? Date.parse(left.optOutTimeUTC) : Number.NaN;
    const rightTime =
      right.detailStatus === 'loaded' && right.optOutTimeUTC ? Date.parse(right.optOutTimeUTC) : Number.NaN;
    const leftHasTime = Number.isFinite(leftTime);
    const rightHasTime = Number.isFinite(rightTime);

    if (leftHasTime && rightHasTime) return rightTime - leftTime;
    if (leftHasTime) return -1;
    if (rightHasTime) return 1;
    return left.appUid.localeCompare(right.appUid);
  });

/** Displays disqualified Horizon applications and their opt-out events for a selected date range. */
const HorizonOptOutTracker = () => {
  const [applicationSearch, setApplicationSearch] = useState('');
  const [draftApplicationSearch, setDraftApplicationSearch] = useState('');
  const [dateRange, setDateRange] = useState<HorizonOptOutDateRange>(getDefaultHorizonOptOutDateRange);
  const [draftDateRange, setDraftDateRange] = useState<HorizonOptOutDateRange>(dateRange);
  const [optOutReason, setOptOutReason] = useState('');
  const [draftOptOutReason, setDraftOptOutReason] = useState('');
  const [region, setRegion] = useState<HorizonRegionFilter>(ALL_HORIZON_REGIONS);
  const [draftRegion, setDraftRegion] = useState<HorizonRegionFilter>(ALL_HORIZON_REGIONS);
  const [triggeredBy, setTriggeredBy] = useState<HorizonTriggeredByFilter>(ALL_TRIGGERED_BY);
  const [draftTriggeredBy, setDraftTriggeredBy] = useState<HorizonTriggeredByFilter>(ALL_TRIGGERED_BY);
  const [areFiltersExpanded, setAreFiltersExpanded] = useState(true);
  const isDateRangeInvalid =
    !draftDateRange.startDate || !draftDateRange.endDate || draftDateRange.startDate > draftDateRange.endDate;
  const {
    data = [],
    hasDetailErrors,
    isEnriching,
    isError,
    isLoading,
    refetch,
    retryDetails,
  } = useHorizonOptOutTracker(dateRange, region);
  const normalizedApplicationSearch = applicationSearch.trim().toLowerCase();
  const normalizedOptOutReason = optOutReason.trim().toLowerCase();
  const filteredData = useMemo(
    () =>
      sortByOptOutTimeDescending(
        data.filter(
          (row) =>
            (!normalizedApplicationSearch ||
              [row.appUid, row.email, row.companyName].some((value) =>
                value.toLowerCase().includes(normalizedApplicationSearch),
              )) &&
            (!normalizedOptOutReason || row.optOutReason?.toLowerCase().includes(normalizedOptOutReason)) &&
            (triggeredBy === ALL_TRIGGERED_BY ||
              (triggeredBy === 'Worth' && row.type === 'Worth') ||
              (triggeredBy === 'Non-Worth' && row.detailStatus === 'loaded' && row.type !== 'Worth')),
        ),
      ),
    [data, normalizedApplicationSearch, normalizedOptOutReason, triggeredBy],
  );

  const applyFilters = () => {
    setApplicationSearch(draftApplicationSearch);
    setDateRange(draftDateRange);
    setOptOutReason(draftOptOutReason);
    setRegion(draftRegion);
    setTriggeredBy(draftTriggeredBy);
    setAreFiltersExpanded(false);
  };

  if (isLoading) return <PageSkeleton label="Loading Horizon opt-outs" variant="table" />;
  if (isError) {
    return (
      <VegaFlex alignItems="start" direction="col" gap="size-12">
        <VegaPageNotification
          duration={0}
          message="The Horizon opt-out data could not be loaded. Try again."
          notificationTitle="Unable to load opt-outs"
          type="error"
        />
        <VegaButton label="Retry" onVegaClick={refetch} size="small" />
      </VegaFlex>
    );
  }

  return (
    <VegaFlex direction="col" gap="size-20">
      <HorizonOptOutSummary isEnriching={isEnriching} rows={filteredData} />
      <HorizonOptOutFilters
        applicationSearch={draftApplicationSearch}
        dateRange={draftDateRange}
        isExpanded={areFiltersExpanded}
        isInvalid={isDateRangeInvalid}
        onApply={applyFilters}
        onApplicationSearchChange={setDraftApplicationSearch}
        onDateRangeChange={setDraftDateRange}
        onExpandedChange={setAreFiltersExpanded}
        onOptOutReasonChange={setDraftOptOutReason}
        onRegionChange={setDraftRegion}
        onTriggeredByChange={setDraftTriggeredBy}
        optOutReason={draftOptOutReason}
        region={draftRegion}
        summary={describeHorizonOptOutFilters({ applicationSearch, dateRange, optOutReason, region, triggeredBy })}
        triggeredBy={draftTriggeredBy}
      />
      {hasDetailErrors && (
        <VegaFlex alignItems="start" direction="col" gap="size-12" data-testid="horizon-detail-error">
          <VegaPageNotification
            duration={0}
            message="Some opt-out details could not be loaded. Retry to load only the rows that failed."
            notificationTitle="Some details are missing"
            type="error"
          />
          <VegaButton disabled={isEnriching} label="Retry details" onVegaClick={retryDetails} size="small" />
        </VegaFlex>
      )}
      <HorizonOptOutTable
        dateRange={dateRange}
        hasSearchFilter={
          normalizedApplicationSearch.length > 0 ||
          normalizedOptOutReason.length > 0 ||
          triggeredBy !== ALL_TRIGGERED_BY
        }
        isEnriching={isEnriching}
        rows={filteredData}
      />
    </VegaFlex>
  );
};

export default HorizonOptOutTracker;
